// Import the pure types modules (not the package barrels) so this stays
// importable from Vitest without dragging the chat runtime in.
import { BUILTIN_TOOLS } from './utils'
import {
  ASK_USER_QUESTION_STAGED_TOOLNAME,
  ASK_USER_QUESTION_STAGED_TOOLSET,
  parseAskUserQuestionInput,
} from './ask-user-question/types'
import {
  ASK_USER_FEEDBACK_STAGED_TOOLNAME,
  ASK_USER_FEEDBACK_STAGED_TOOLSET,
  feedbackQuestionText,
  parseAskUserFeedbackInput,
} from './ask-user-feedback/types'

/**
 * The result the backend's execute path writes for an ask-user call it turned
 * into a staged action (`{ surfaced, stagedActionId?, note }`). This is the
 * current contract in every context — foreground chats included — so a call
 * carrying it must be answered through the staged action's `/answer` endpoint,
 * never by posting a tool-result. `surfaced: false` means the question was
 * never delivered (no conversation context) and nothing should render for it.
 */
export interface AskUserSurfacedResult {
  surfaced: boolean
  stagedActionId?: string
}

export function parseAskUserSurfacedResult(result: unknown): AskUserSurfacedResult | null {
  if (!result) return null
  let parsed: unknown = result
  try {
    parsed = typeof result === 'string' ? JSON.parse(result) : result
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || typeof (parsed as { surfaced?: unknown }).surfaced !== 'boolean') {
    return null
  }
  const stagedActionId = (parsed as { stagedActionId?: unknown }).stagedActionId
  return {
    surfaced: (parsed as { surfaced: boolean }).surfaced,
    stagedActionId: typeof stagedActionId === 'string' ? stagedActionId : undefined,
  }
}

/**
 * A bridging snapshot of the staged action behind a surfaced ask-user call —
 * enough identity for the thread to draw the card (and recognize it as
 * answerable) before the staged-actions query has the live row. Mirrors the
 * backend's title rule: the first question's text, kept list-row short.
 */
export interface AskUserStagedActionSnapshot {
  id: string
  toolSet: string
  toolName: string
  title?: string
}

const SNAPSHOT_TITLE_MAX = 80

function snapshotTitle(text: string | undefined): string | undefined {
  const trimmed = text?.trim()
  if (!trimmed) return undefined
  return trimmed.length > SNAPSHOT_TITLE_MAX ? `${trimmed.slice(0, SNAPSHOT_TITLE_MAX - 3)}…` : trimmed
}

/**
 * Resolve an ask-user tool call to its surfaced staged action, or null for
 * anything that put nothing in front of the user: other tools, the legacy
 * inline flavor (no surfaced marker — the tool-result card owns it), and
 * undelivered calls (`surfaced: false`).
 */
export function askUserStagedActionSnapshot(toolCall: {
  toolName?: string
  toolInput?: unknown
  result?: unknown
}): AskUserStagedActionSnapshot | null {
  const isQuestion = toolCall.toolName === BUILTIN_TOOLS.ASK_USER_QUESTION
  const isFeedback = toolCall.toolName === BUILTIN_TOOLS.ASK_USER_FEEDBACK
  if (!isQuestion && !isFeedback) return null

  const surfaced = parseAskUserSurfacedResult(toolCall.result)
  if (!surfaced?.surfaced || !surfaced.stagedActionId) return null

  if (isQuestion) {
    const input = parseAskUserQuestionInput(toolCall.toolInput)
    return {
      id: surfaced.stagedActionId,
      toolSet: ASK_USER_QUESTION_STAGED_TOOLSET,
      toolName: ASK_USER_QUESTION_STAGED_TOOLNAME,
      title: snapshotTitle(input?.intro?.trim() || input?.questions[0]?.question),
    }
  }

  const input = parseAskUserFeedbackInput(toolCall.toolInput)
  const firstBlank = input?.blanks[0]
  return {
    id: surfaced.stagedActionId,
    toolSet: ASK_USER_FEEDBACK_STAGED_TOOLSET,
    toolName: ASK_USER_FEEDBACK_STAGED_TOOLNAME,
    title: snapshotTitle(input?.about?.trim() || (firstBlank ? feedbackQuestionText(firstBlank) : undefined)),
  }
}

/** An ask-user call whose input has finished streaming — enough to draw a card from. */
function hasReadableAskUserInput(toolCall: { toolName?: string; toolInput?: unknown }): boolean {
  if (toolCall.toolName === BUILTIN_TOOLS.ASK_USER_QUESTION) {
    return parseAskUserQuestionInput(toolCall.toolInput) != null
  }
  if (toolCall.toolName === BUILTIN_TOOLS.ASK_USER_FEEDBACK) {
    return parseAskUserFeedbackInput(toolCall.toolInput) != null
  }
  return false
}

/**
 * An ask-user call the backend is still executing. It is on its way to a
 * surfaced marker — `surfaceBackgroundQuestion` stages the action and answers
 * with one in every context — so the thread must not draw an answer card for it
 * yet: the card's home is the floating bar, and drawing it here first only to
 * tear it down when the marker lands is the flicker this predicate exists to
 * prevent. Worse, an answer submitted in that window takes the tool-result path
 * the surfaced contract forbids (see `AskUserSurfacedResult`). Callers render
 * the same waiting state a surfaced-but-unanswered call gets.
 */
export function isAwaitingAskUserResult(toolCall: { toolName?: string; result?: unknown }): boolean {
  if (toolCall.result) return false
  return isAskUserToolCall(toolCall)
}

/** Either ask-user tool, in any state. The backend ends the turn on these calls. */
export function isAskUserToolCall(toolCall: { toolName?: string }): boolean {
  return toolCall.toolName === BUILTIN_TOOLS.ASK_USER_QUESTION || toolCall.toolName === BUILTIN_TOOLS.ASK_USER_FEEDBACK
}

/**
 * Whether the thread draws this call's own answer card: the legacy inline
 * flavor, answered by posting a tool-result. `askUserStagedActionSnapshot`
 * returns null for those, so without this they'd be treated as ordinary tool
 * calls and buried inside a collapsed tool group with the turn still waiting on
 * an answer. A call still executing belongs to `isAwaitingAskUserResult`, not
 * here — before the result lands the two flavors are indistinguishable, and
 * guessing "inline" is wrong for every call the current backend executes.
 */
export function rendersInlineAskUserCard(toolCall: {
  toolName?: string
  toolInput?: unknown
  result?: unknown
}): boolean {
  if (isAwaitingAskUserResult(toolCall)) return false
  if (parseAskUserSurfacedResult(toolCall.result)) return false
  return hasReadableAskUserInput(toolCall)
}
